import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component'
import {
    IconEdit, IconTrash,
    IconUser, IconBriefcase, IconPercentage, IconLink
} from '@tabler/icons-react'
import toast from 'react-hot-toast'
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal'
import { hsclLogo1, sanhdef1 } from '../../assets/images'
import { useAuth } from '../../contexts/AuthContext'
import {
    useEmployees, useDeleteEmployee, useEmployeeProjects,
    useDownloadEmployees, useCrossOrgEmployees, useCrossOrgAccess
} from '../../hooks/useEmployees'
import { useProjects } from '../../hooks/useProjects'
import { useOrganizations } from '../../hooks/useOrganizations'
import AssignProjectModal from './components/AssignProjectModal'
import AssignAccessModal from './components/AssignAccessModal'
import ActionDropdown from '../../components/common/ActionDropdown'
import MultiSelect from '../../components/common/MultiSelect'

const getOrgLogos = (org) => {
    if (!org) return [];
    const name = org.toLowerCase();

    if (name.includes('hscl') && name.includes('sanhdef')) {
        return [hsclLogo1, sanhdef1];
    }

    if (name.includes('sanhdef')) return [sanhdef1];
    return [hsclLogo1];
};

export default function Employee() {
    const navigate = useNavigate()
    const { user, setSelectedOrg } = useAuth()
    const orgId = user?.orgId

    const { data: orgsData } = useOrganizations()
    const organizations = orgsData?.results || orgsData || []

    const [assignModalOpen, setAssignModalOpen] = useState(false)
    const [selectedEmployeeForAssignment, setSelectedEmployeeForAssignment] = useState(null)

    const [accessModalOpen, setAccessModalOpen] = useState(false)
    const [selectedEmployeeForAccess, setSelectedEmployeeForAccess] = useState(null)

    // Auto-select first org if superuser and none selected
    if (user?.is_superuser && !orgId && organizations.length > 0) {
        // We can't call setSelectedOrg in render, but we can use the ID for the hooks immediately
        // and effect will update context later if needed, or we just rely on local variables
    }

    const effectiveOrgId = orgId || ((user?.is_superuser && organizations.length > 0) ? organizations[0].id : null)

    const { data: employees = [], isLoading: isLoadingEmployees } = useEmployees(effectiveOrgId)
    const { data: projects = [], isLoading: isLoadingProjects } = useProjects(effectiveOrgId)
    const { data: allAssignments = [] } = useEmployeeProjects(effectiveOrgId)
    const deleteEmployeeMutation = useDeleteEmployee()
    const downloadMutation = useDownloadEmployees()

    const [filterText, setFilterText] = useState('')
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

    // Action Dropdown State is self-contained

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const handleDelete = (row) => {
        setItemToDelete(row);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteEmployeeMutation.mutateAsync({ id: itemToDelete.id, orgId: effectiveOrgId });
            toast.success(`Employee "${itemToDelete.name}" deleted successfully`);
            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting employee:', error);
        }
    };

    const handleDownload = async () => {
        try {
            await downloadMutation.mutateAsync({ orgId: effectiveOrgId });
            toast.success('Download Successful');
        } catch (error) {
            console.error('Error downloading employees:', error);
            toast.error('Failed to download employees');
        }
    };

    const getProjectById = (id) => projects.find(p => p.id === id)

    // Filter employees
    const filteredEmployees = employees.filter(emp =>
        emp.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        emp.employeeId?.toLowerCase().includes(filterText.toLowerCase()) ||
        emp.role?.toLowerCase().includes(filterText.toLowerCase())
    )


    // Prepare table data with pre-rendered cells
    const tableData = filteredEmployees.map(emp => {
        // Merge assignments from separate query
        const empAssignments = allAssignments.filter(a => a.employee === emp.id)
        // check if emp.projectAssignments exists
        const nestedAssignments = emp.projectAssignments || []
        const tempProjectAssignments = empAssignments.length > 0 ? empAssignments : nestedAssignments

        const totalLOE = tempProjectAssignments.reduce((sum, a) => sum + parseFloat(a.default_loe_percent || a.loe || 0), 0)
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4']

        return {
            ...emp,
            avatarCell: (() => {
                const initials = emp.name
                    ? emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : '?';
                const bg = colors[(emp.name?.charCodeAt(0) || 0) % colors.length];
                return (
                    <div style={{ backgroundColor: bg }} className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {initials}
                    </div>
                );
            })(),
            nameCell: (
                <div>
                    <p className="font-medium text-sm">{emp.name}</p>
                    <p className="text-[11px] text-text-secondary">{emp.employeeId}</p>
                </div>
            ),
            loeCell: tempProjectAssignments.length > 0 ? (
                <div className="w-full">
                    <div className="flex h-4 rounded-full overflow-hidden bg-bg-light mb-2">
                        {tempProjectAssignments.map((assignment, idx) => {
                            const projectId = typeof assignment.project === 'object' ? assignment.project.id : assignment.project;
                            const project = getProjectById(projectId)
                            const loe = parseFloat(assignment.default_loe_percent || assignment.loe || 0);

                            return (
                                <div
                                    key={idx}
                                    className={`${colors[idx % colors.length]} h-full`}
                                    style={{ width: `${loe}%` }}
                                    title={`${project?.name || 'Project'}: ${loe}%`}
                                />
                            )
                        })}
                    </div>
                    <div className="flex flex-wrap gap-x-6">
                        {tempProjectAssignments.map((assignment, idx) => {
                            const projectId = typeof assignment.project === 'object' ? assignment.project.id : assignment.project;
                            const project = getProjectById(projectId)
                            const loe = parseFloat(assignment.default_loe_percent || assignment.loe || 0);

                            return (
                                <span key={idx} className="inline-flex items-center gap-3 text-[10px]">
                                    <span className={`w-[6px] h-[6px] rounded-full ${colors[idx % colors.length]}`}></span>
                                    <span className="text-text-secondary">{project?.code || project?.name?.substring(0, 3) || 'PRJ'}: {loe}%</span>
                                </span>
                            )
                        })}
                    </div>
                </div>
            ) : (
                <span className="text-[11px] text-text-secondary italic">No assignments</span>
            ),
            totalLoeCell: (
                <span className={`text-sm font-bold ${totalLOE === 100 ? 'text-success' : totalLOE > 100 ? 'text-danger' : 'text-warning'}`}>
                    {totalLOE}%
                </span>
            ),
            actionCell: (
                <div className="flex justify-center">
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'Assign Project',
                                icon: IconBriefcase,
                                onClick: () => {
                                    setSelectedEmployeeForAssignment(emp);
                                    setAssignModalOpen(true);
                                },
                                description: "Assign this employee to a project"
                            },
                            ...(user?.is_superuser ? [{
                                label: 'Assign Access',
                                icon: IconLink,
                                onClick: () => {
                                    setSelectedEmployeeForAccess(emp);
                                    setAccessModalOpen(true);
                                },
                                description: "Grant organization access to this employee"
                            }] : []),
                            {
                                label: 'Edit Profile',
                                icon: IconEdit,
                                onClick: () => navigate(`/edit-employee/${emp.id}`),
                                description: "Modify employee personal and job details"
                            },
                            {
                                label: 'Delete Employee',
                                icon: IconTrash,
                                danger: true,
                                onClick: () => handleDelete(emp),
                                description: "Permanently remove this record"
                            }
                        ]}
                    />
                </div>
            ),
        }
    })

    // Table columns
    const columns = [
        {
            name: 'Avatar',
            selector: row => row.avatarCell,
            width: '80px',
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            sortFunction: (a, b) => a.name.localeCompare(b.name),
        },
        {
            name: 'Role',
            selector: row => row.role,
            sortable: true,
        },
        {
            name: 'LOE Distribution',
            selector: row => row.loeCell,
            width: '250px',
        },
        {
            name: 'Total LOE',
            selector: row => row.totalLoeCell,
            sortable: true,
            sortFunction: (a, b) => {
                const aLoe = (a.projectAssignments || []).reduce((sum, x) => sum + x.loe, 0)
                const bLoe = (b.projectAssignments || []).reduce((sum, x) => sum + x.loe, 0)
                return aLoe - bLoe
            },
            center: true,
            width: '100px',
        },
        {
            name: 'Action',
            selector: row => row.actionCell,
            right: true,
        },
    ]

    // Search header component
    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle)
                setFilterText('')
            }
        }

        return (
            <div className='flex gap-5 mb-10 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search here...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">
                    Clear
                </button>
            </div>
        )
    }, [filterText, resetPaginationToggle])

    // Calculate project totals (for summary)
    const projectTotals = projects.map(project => {
        const totalLOE = employees.reduce((sum, emp) => {
            const assignment = (emp.projectAssignments || []).find(a => a.projectId === project.id)
            return sum + (assignment?.loe || 0)
        }, 0)
        const employeeCount = employees.filter(emp =>
            (emp.projectAssignments || []).some(a => a.projectId === project.id)
        ).length
        return { ...project, totalLOE, employeeCount }
    }).filter(p => p.employeeCount > 0)

    if (isLoadingEmployees || isLoadingProjects) {
        return <div className="p-20">Loading...</div>
    }

    return (
        <>
            <Breadcrumb title="All Employees" path="Employees / All Employees" />

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-20 mb-20">
                <div className="bg-bg-dark rounded-lg shadow-light p-20">
                    <div className="flex items-center gap-15">
                        <div className="w-50 h-50 rounded-xl bg-success/10 flex items-center justify-center">
                            <IconUser size={24} className="text-success" />
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase font-semibold">Total Employees</p>
                            <p className="text-2xl font-bold">{employees.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-bg-dark rounded-lg shadow-light p-20">
                    <div className="flex items-center gap-15">
                        <div className="w-50 h-50 rounded-xl bg-info/10 flex items-center justify-center">
                            <IconBriefcase size={24} className="text-info" />
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase font-semibold">Active Projects</p>
                            <p className="text-2xl font-bold">{projectTotals.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-bg-dark rounded-lg shadow-light p-20">
                    <div className="flex items-center gap-15">
                        <div className="w-50 h-50 rounded-xl bg-warning/10 flex items-center justify-center">
                            <IconPercentage size={24} className="text-warning" />
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase font-semibold">Avg LOE/Employee</p>
                            <p className="text-2xl font-bold">
                                {employees.length > 0 ? Math.round(employees.reduce((sum, e) => sum + (e.projectAssignments || []).reduce((s, a) => s + a.loe, 0), 0) / employees.length) : 0}%
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-bg-dark rounded-lg shadow-light p-20">
                    <div className="flex items-center gap-15">
                        <div className="w-50 h-50 rounded-xl bg-danger/10 flex items-center justify-center">
                            <IconUser size={24} className="text-danger" />
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase font-semibold">Unassigned</p>
                            <p className="text-2xl font-bold">
                                {employees.filter(e => (e.projectAssignments || []).length === 0).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Table Card */}
            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className='flex items-center justify-between gap-15 mb-30'>
                    <p className='text-[16px]/[19.2px] font-medium'>
                        Employee Project Assignments
                    </p>
                    <div className='flex items-center gap-10'>
                        <button
                            onClick={handleDownload}
                            className='btn btn-outline-primary'
                            disabled={downloadMutation.isPending}
                        >
                            {downloadMutation.isPending ? 'Downloading...' : 'Download Employees'}
                        </button>
                        <button onClick={() => navigate('/add-employee')} className='btn btn-primary'>
                            Add New
                        </button>
                    </div>
                </div>
                <div className='react-data-table'>
                    <ReactDataTable
                        columns={columns}
                        data={tableData}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponentMemo}
                        onRowClicked={(row) => navigate(`/employee/${row.id}/snapshot`)}
                        pointerOnHover
                        highlightOnHover
                    />
                </div>
            </div>

            {/* Project Summary Section */}
            {/* <div className="mt-20 bg-bg-dark rounded-lg shadow-light p-20">
                <p className="text-lg font-semibold mb-15">Project LOE Summary</p>
                <div className="grid grid-cols-2 gap-15">
                    {projectTotals.map((project) => (
                        <div key={project.id} className="flex items-center gap-15 p-15 bg-bg-light rounded-lg">
                            <div className="flex-grow">
                                <div className="flex items-center gap-8 mb-4">
                                    <span className="font-medium text-sm">{project.name}</span>

                                    <div className="flex items-center gap-4">
                                        {getOrgLogos(project.org).map((logo, index) => (
                                            <div key={index} className="h-14 w-auto flex items-center justify-center bg-white px-4 py-1 rounded border border-gray-200 shadow-sm">
                                                <img
                                                    src={logo}
                                                    alt="organization logo"
                                                    className="h-full w-auto object-contain"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-xs text-text-secondary">{project.employeeCount} employee(s) assigned</p>
                            </div>
                            <div className="text-right">
                                <p className="text-lg font-bold text-primary">{project.totalLOE}%</p>
                                <p className="text-xs text-text-secondary">Total LOE</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div> */}

            {/* Modals */}
            <AssignProjectModal
                isOpen={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                employee={selectedEmployeeForAssignment}
            />

            <AssignAccessModal
                isOpen={accessModalOpen}
                onClose={() => { setAccessModalOpen(false); setSelectedEmployeeForAccess(null); }}
                employee={selectedEmployeeForAccess}
            />

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title="Delete Employee"
                message="Are you sure you want to delete this employee? This action will remove the employee record and all project assignments."
                itemName={itemToDelete?.name}
                isLoading={deleteEmployeeMutation.isPending}
            />
        </>
    )
}
